'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useProjectEstimates,
  type EstimateSummary,
} from '@/features/design/estimate';

/** Request fields the picker contributes to the album generate payload. */
export type AlbumEstimatePayload =
  | { estimate_id: string }
  | { generate_new_estimate: true }
  | { include_estimate: false };

export interface AlbumEstimatePicker {
  /** Ready estimates of THIS design — listed first; the default selection. */
  currentDesignEstimates: EstimateSummary[];
  /** Ready estimates of sibling designs — greyed tail options. */
  otherDesignEstimates: EstimateSummary[];
  /** Selected ready estimate id, or null = "New estimate". Only meaningful
   *  while `isEstimateIncluded` is true. */
  selectedEstimateId: string | null;
  /** Pick an estimate (or null = "New estimate") — implies inclusion. */
  setSelectedEstimateId: (id: string | null) => void;
  /** False = the "Don't include" picker option: the album skips the cost
   *  sections entirely. */
  isEstimateIncluded: boolean;
  /** Select the "Don't include" option. */
  excludeEstimate: () => void;
  /**
   * Fields to spread into the album generate request. Selected estimate →
   * `estimate_id`; "New estimate" → `generate_new_estimate: true`;
   * "Don't include" → `include_estimate: false`. Estimate is INCLUDED by
   * default (the current design's ready estimate, else "New estimate"); the
   * server decides whether to embed it or return the album paywall on Generate.
   */
  estimatePayload: AlbumEstimatePayload;
}

/**
 * Album Estimate picker state (FE4), shared by the single-column panel
 * (`useAlbumPanel`/`AlbumPanelSettings`) and the two-column drill-down
 * (`AlbumTabContent`).
 *
 * Sources the PROJECT-scoped estimate list so sibling designs' estimates are
 * offered too, and defaults the selection to this design's latest ready
 * estimate (none → "New estimate", which generates a fresh one inside the
 * album pipeline). The "Don't include" option opts the album out of the cost
 * sections entirely. No client-side plan gate post-pivot — estimate is
 * included by default and the server returns the album paywall on Generate
 * for non-pack users.
 */
export function useAlbumEstimatePicker(
  projectId: string,
  conceptId: string
): AlbumEstimatePicker {
  const { data: projectEstimates } = useProjectEstimates(projectId);
  const readyEstimates = useMemo<EstimateSummary[]>(
    () => (projectEstimates ?? []).filter((e) => e.status === 'ready'),
    [projectEstimates]
  );
  // Server order is newest-first, so index 0 is the latest ready estimate.
  const currentDesignEstimates = useMemo(
    () => readyEstimates.filter((e) => e.conceptId === conceptId),
    [readyEstimates, conceptId]
  );
  const otherDesignEstimates = useMemo(
    () => readyEstimates.filter((e) => e.conceptId !== conceptId),
    [readyEstimates, conceptId]
  );

  const [selectedEstimateId, setSelectedEstimateIdState] = useState<
    string | null
  >(null);
  const [isEstimateIncluded, setIsEstimateIncluded] = useState(true);

  // Picking an estimate / "New estimate" implies inclusion — "Don't include"
  // is left via any other option.
  const setSelectedEstimateId = (id: string | null) => {
    setSelectedEstimateIdState(id);
    setIsEstimateIncluded(true);
  };
  const excludeEstimate = () => setIsEstimateIncluded(false);

  // Default once the list arrives: this design's latest ready estimate, else
  // "New estimate". Only on first load — later list updates must not override
  // an explicit user choice.
  const hasDefaulted = useRef(false);
  useEffect(() => {
    if (hasDefaulted.current || !projectEstimates) return;
    hasDefaulted.current = true;
    setSelectedEstimateIdState(currentDesignEstimates[0]?.id ?? null);
  }, [projectEstimates, currentDesignEstimates]);

  // Stale guard: the chosen estimate left the ready list (deleted /
  // regenerating via WS) → fall back to this design's latest ready estimate,
  // else "New estimate" — never send a stale id.
  useEffect(() => {
    if (
      selectedEstimateId &&
      !readyEstimates.some((e) => e.id === selectedEstimateId)
    ) {
      setSelectedEstimateIdState(currentDesignEstimates[0]?.id ?? null);
    }
  }, [selectedEstimateId, readyEstimates, currentDesignEstimates]);

  const estimatePayload = useMemo<AlbumEstimatePayload>(() => {
    // Estimate is included by default; the user opts out via "Don't include".
    if (!isEstimateIncluded) return { include_estimate: false };
    if (selectedEstimateId) return { estimate_id: selectedEstimateId };
    return { generate_new_estimate: true };
  }, [isEstimateIncluded, selectedEstimateId]);

  return {
    currentDesignEstimates,
    otherDesignEstimates,
    selectedEstimateId,
    setSelectedEstimateId,
    isEstimateIncluded,
    excludeEstimate,
    estimatePayload,
  };
}
